<template>
  <div
    id="location-details"
    class="location-details"
    data-ui="location-details"
  >
    <!-- location name, address and directions link -->
    <div
      v-if="showMeetAtLocation"
      class="location-details__header"
      data-ui="location-details-meet-at-active"
    >
      <div class="location-details__info">
        <h3 class="location-details__subheading">Meet at</h3>
        <component
          :is="dynamicTag"
          :data-ui="`location-details-meet-at-${meetAtType}`"
          class="location-details__meet-at-address"
        >
          <CdrLink
            :href="meetAtNameLinkConfig.href"
            class="location-details__meet-at-link"
            data-ui="event-details-meet-at-link"
            target="_blank"
            @click="() => handleMetrics('meet at location')"
          >
            {{ meetAtNameLinkConfig.text }}
          </CdrLink>
          <CdrText
            v-if="meetAtHasAddress"
            class="location-details__meet-at-address-text"
            data-ui="event-details-meeting-location-address"
          >
            {{ meetAtAddress }}
          </CdrText>
        </component>
      </div>
      <div class="location-details__directions">
        <!-- meet at directions link -->
        <CdrButton
          :full-width="true"
          :href="meetAtDirectionsUrl"
          class="location-details__directions-link"
          data-ui="event-details-meeting-directions-link"
          modifier="secondary"
          size="small@xs"
          tag="a"
          rel="nofollow"
          target="_blank"
          :aria-label="getDirectionsAriaLabel"
          @click="() => handleMetrics('meet at get directions')"
        >
          Get directions
        </CdrButton>
      </div>
      <div class="location-details__meet-activity-location">
        <!-- actual activity location -->
        <h3 class="location-details__subheading">Activity location</h3>
        <CdrLink
          :href="mapHeadingConfig.href"
          class="location-details__map-heading"
          data-ui="event-details-meet-at-activity-location"
          rel="nofollow"
          target="_blank"
          @click="() => handleMetrics('meet at activity location')"
        >
          {{ mapHeadingConfig.text }}
        </CdrLink>
      </div>
    </div>

    <div
      v-else
      class="location-details__header"
      data-ui="location-details-meet-at-inactive"
    >
      <div class="location-details__info">
        <h3 class="location-details__subheading">Activity location</h3>
        <CdrLink
          :href="displayLocationUrl"
          class="location-details__location-name"
          data-ui="event-details-location-name"
          target="_blank"
          @click="() => handleMetrics('activity location')"
        >
          {{ displayLocation.name }}
        </CdrLink>
        <address
          v-if="locationIsStore"
          class="location-details__address"
          data-ui="location-details-address"
        >
          <CdrText
            class="location-details__address-text"
            data-ui="event-details-location-address"
          >
            {{ storeLocationAddress }}
          </CdrText>
        </address>
      </div>
      <div class="location-details__directions">
        <!-- get directions link -->
        <CdrButton
          :full-width="true"
          :href="mapHeadingConfig.href"
          class="location-details__directions-link"
          data-ui="event-details-get-directions-link"
          modifier="secondary"
          size="small@xs"
          tag="a"
          rel="nofollow"
          target="_blank"
          :aria-label="getDirectionsAriaLabel"
          @click="() => handleMetrics('activity get directions')"
        >
          Get directions
        </CdrButton>
      </div>
    </div>

    <!-- map image link -->
    <CdrLink
      :href="directionsUrl"
      :data-latitude="gpsLat"
      :data-longitude="gpsLong"
      class="location-details__map"
      data-ui="location-details-map-link"
      modifier="standalone"
      target="_blank"
      rel="nofollow"
      @click="() => handleMetrics('directions map link')"
    >
      <img
        :src="locationGoogleMapsUrl"
        :alt="`Map image of ${displayLocation.name}`"
        data-ui="location-details-map"
        class="location-details__map-image"
      />
    </CdrLink>

    <!-- more directions link -->
    <div
      v-if="displayLocation.directions"
      class="location-details__moar"
      data-ui="location-details-moar"
    >
      <h3 class="location-details__moar-subheading">More directions and information</h3>
      <CdrText>{{ displayLocation.directions }}</CdrText>
    </div>
  </div>
</template>

<script>
import { CdrButton, CdrIcon, CdrText, CdrList, CdrLink } from '@rei/cedar';
import metrics from '../mixins/metrics';

export default {
  name: 'SessionLocationDetailsComponent',
  components: {
    CdrButton,
    CdrIcon,
    CdrText,
    CdrList,
    CdrLink,
  },
  mixins: [metrics],
  props: {
    displayLocation: {
      type: Object,
      required: true,
    },
    displayMeetingLocation: {
      type: Object,
      required: true,
    },
    gpsLat: {
      type: Number,
      required: true,
    },
    gpsLong: {
      type: Number,
      required: true,
    },
    locationGoogleMapsUrl: {
      type: String,
      required: true,
    },
    locationIsStore: {
      type: Boolean,
      required: true,
    },
    meetAtStoreUrl: {
      type: [Boolean, String],
      required: true,
    },
    meetAtLocationIsStore: {
      type: Boolean,
      required: true,
    },
    meetAtMapDirectionsUrl: {
      type: String,
      required: true,
    },
    showMeetAtLocation: {
      type: Boolean,
      required: true,
    },
    storeUrl: {
      type: [Boolean, String],
      required: true,
    },
  },
  computed: {
    /*
      returns <String> used by google to render map image
    */
    directionsUrl() {
      const { meetAtType } = this;
      const locationType = meetAtType && meetAtType !== 'store-address' ? 'session' : 'other-venue';
      return this.getAddressUrl(locationType);
    },
    displayLocationUrl() {
      const { locationIsStore, storeUrl } = this;
      if (locationIsStore && storeUrl !== false) {
        return storeUrl;
      }
      return this.mapHeadingConfig.href;
    },
    dynamicTag() {
      const { meetAtHasAddress } = this;
      return meetAtHasAddress ? 'address' : 'div';
    },
    /*
      <object> with text and link url for the link directly above the map image
      text should be the name of the session location unless the session and the meet at locations are the same
    */
    mapHeadingConfig() {
      const { displayLocation, directionsUrl: href } = this;
      const { name: text } = displayLocation;
      return { href, text };
    },
    /*
      <boolean> indicating if there is an address to display. If the meetAtLcation is a store, it will
      always have an address. If it is a venue, there's a chance it will not have an address
    */
    meetAtHasAddress() {
      const { displayMeetingLocation: meetAt, meetAtLocationIsStore } = this;
      if (meetAtLocationIsStore || (meetAt && meetAt.address)) {
        return true;
      }
      return false;
    },
    /*
      <object> containing props for the "Meet at:" link
      only used when prop showMeetAtLocation is true
    */
    meetAtNameLinkConfig() {
      const { displayMeetingLocation, meetAtStoreUrl } = this;
      const { name: text } = displayMeetingLocation;
      const href = meetAtStoreUrl || this.getAddressUrl('other-venue');
      return { text, href };
    },
    meetAtAddress() {
      const { displayMeetingLocation } = this;
      // eslint-disable-next-line
      const { address, address2, city, stateName, zip } = displayMeetingLocation;
      return (
        [address, address2, city, `${stateName} ${zip}`]
          // filter to remove address2 if it is not present
          .filter((it) => !!it)
          .join(', ')
      );
    },
    meetAtDirectionsUrl() {
      return this.getAddressUrl('meet-at');
    },
    meetAtType() {
      const {
        displayLocation = {},
        displayMeetingLocation = {},
        meetAtLocationIsStore,
        showMeetAtLocation,
      } = this;
      if (!showMeetAtLocation) return false;
      if (meetAtLocationIsStore) {
        return 'store-address';
      }
      if (
        displayLocation.id &&
        displayMeetingLocation.id &&
        displayLocation.id === displayMeetingLocation.id
      ) {
        return 'session-venue';
      }
      return 'other-venue';
    },
    storeLocationAddress() {
      // eslint-disable-next-line
      const { displayLocation: { address, city, stateName, zip } } = this;
      return [address, city, `${stateName} ${zip}`].join(', ');
    },
    getDirectionsAriaLabel() {
      const {
        displayLocation: { name },
      } = this;
      return `Get directions${name ? ` to ${name}` : ''}`;
    },
  },
  methods: {
    /*
      Formerly mapDirectionsUrl
      returns <String> used by google to render map image
      prefer to use address if available; use gps coords if not
    */
    getAddressUrl(type /* = 'meet-at' */) {
      const { displayLocation, displayMeetingLocation, meetAtType } = this;

      let selectedLocation = displayLocation;
      if (type === 'meet-at') {
        selectedLocation = displayMeetingLocation;
      } else if (type === 'other-venue') {
        // if there is actually a 'meet at' location & it's not the store
        if (meetAtType && meetAtType !== 'store-address') {
          selectedLocation = displayMeetingLocation;
        }
      }

      // eslint-disable-next-line
      const { address, address2, city, stateName, zip, gpsLat, gpsLong } = selectedLocation;
      return address
        ? `https://maps.google.com?daddr=+${address}+${city}+${stateName}+${zip}`
        : `https://maps.google.com?daddr=${gpsLat},${gpsLong}&zoom=13&size=400x400`;
    },
    handleMetrics(str = 'Get directions') {
      const linkName = `{{ pageName }}_directions_${str}`;
      this.metricsClick({ linkName });
    },
  },
};
</script>
